import { SketchGroup } from '../../../mesh/sketch/SketchGroup';
import type { SketchPointObject } from '../../../mesh/sketch/SketchPointObject';
import { SketchSplineObject } from '../../../mesh/sketch/SketchSplineObject';
import { SketchStyleSplineObject } from '../../../mesh/sketch/SketchStyleSplineObject';
import { isPointCoincidentConstraint } from '../../../sketch/solver/constraint/relationDefinition/point/PointCoincidentConstraint';
import { SKETCH_GEOMETRY_TOLERANCE_CONFIG } from '../../../sketch/config/SketchGeometryToleranceConfig';
import type { TargetAnchorBindings } from '../model/TargetAnchorBindings';
import { SketchReferenceTargetEndpointCanonicalization } from './SketchReferenceTargetEndpointCanonicalization';
import type {
	SketchReferenceTargetEndpointTopologyAssignment,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetEndpointTopology';
import type {
	SketchReferenceEndpointCanonicalizationAssignment,
	SketchReferenceTargetAnchorBindingChange,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetEndpointCanonicalization';

/**
 * 转换实体引用目标端点拓扑统一器。
 *
 * 只消费来源端点的稳定锚点身份，不读取坐标。相同 topology vertex 或来源共享草图点
 * 映射为一个目标点；来源点身份不同的显式重合关系仍交给关系恢复服务处理。
 */
export class SketchReferenceTargetEndpointCanonicalizer {
	/**
	 * 识别不能继续共享同一个旧目标点的引用。
	 *
	 * 同一个目标点在上一轮可能代表一个共享来源 vertex；来源边拆分后，该点可能同时被映射到
	 * 多个不同的新 vertex。此时只保留匹配证据最强的一组原位实体，其余引用必须走目标替换，
	 * 让后续批次 canonicalize 按新的来源锚点重新合并正确端点。
	 *
	 * @param assignments 当前面边界匹配形成的目标端点拓扑归属。
	 * @returns 必须退出原位复用的引用 ID，按字典序稳定输出。
	 */
	public resolveUnsafeInPlaceReferenceIds(
		assignments: readonly SketchReferenceTargetEndpointTopologyAssignment[],
	): ReadonlySet<string> {
		// 先按旧目标点分组，定位一个物理点是否被分配给多个当前来源 vertex。
		const assignmentsByTargetPointId = new Map<
			string,
			SketchReferenceTargetEndpointTopologyAssignment[]
		>();
		assignments.forEach((assignment) => {
			const pointAssignments = assignmentsByTargetPointId.get(
				assignment.targetPoint.uuid,
			) ?? [];
			pointAssignments.push(assignment);
			assignmentsByTargetPointId.set(
				assignment.targetPoint.uuid,
				pointAssignments,
			);
		});

		const unsafeReferenceIds = new Set<string>();
		assignmentsByTargetPointId.forEach((pointAssignments) => {
			// 同一旧点内部再按当前来源 vertex 分区；只有跨分区共享才是不安全复用。
			const assignmentsBySourceAnchorId = new Map<
				string,
				SketchReferenceTargetEndpointTopologyAssignment[]
			>();
			pointAssignments.forEach((assignment) => {
				const anchorAssignments = assignmentsBySourceAnchorId.get(
					assignment.sourceAnchorId,
				) ?? [];
				anchorAssignments.push(assignment);
				assignmentsBySourceAnchorId.set(
					assignment.sourceAnchorId,
					anchorAssignments,
				);
			});
			if (assignmentsBySourceAnchorId.size <= 1) {
				return;
			}

			// 证据最强的 vertex 分区继承旧点，其余分区通过 replacement 获得独立目标点。
			const rankedAnchorGroups = [...assignmentsBySourceAnchorId.entries()]
				.map(([sourceAnchorId, anchorAssignments]) => ({
					sourceAnchorId,
					anchorAssignments,
					evidence: Math.max(
						...anchorAssignments.map(assignment => assignment.evidence),
					),
					referencePath: [...new Set(
						anchorAssignments.map(assignment => assignment.referenceId),
					)].sort().join('|'),
				}))
				.sort((left, right) => (
					right.evidence - left.evidence
					|| left.referencePath.localeCompare(right.referencePath)
					|| left.sourceAnchorId.localeCompare(right.sourceAnchorId)
				));
			rankedAnchorGroups.slice(1).forEach((group) => {
				group.anchorAssignments.forEach((assignment) => {
					unsafeReferenceIds.add(assignment.referenceId);
				});
			});
		});

		/**
		 * 同一当前来源锚点若对应多个独立样条端点，公共样条 setter 不能用于事务内身份改绑。
		 * 保留证据最强的样条点，其余样条引用必须在上层走 replacement rebuild；普通边随后可
		 * 安全改绑到保留的样条点。
		 */
		const assignmentsBySourceAnchorId = new Map<
			string,
			SketchReferenceTargetEndpointTopologyAssignment[]
		>();
		assignments.forEach((assignment) => {
			const anchorAssignments = assignmentsBySourceAnchorId.get(assignment.sourceAnchorId) ?? [];
			anchorAssignments.push(assignment);
			assignmentsBySourceAnchorId.set(assignment.sourceAnchorId, anchorAssignments);
		});
		assignmentsBySourceAnchorId.forEach((anchorAssignments) => {
			const splineAssignmentsByTargetPointId = new Map<
				string,
				SketchReferenceTargetEndpointTopologyAssignment[]
			>();
			anchorAssignments.forEach((assignment) => {
				const ownsSplineEndpoint = assignment.targetPoint.getConnectedEdgesSnapshot().some(edge => (
					edge instanceof SketchSplineObject || edge instanceof SketchStyleSplineObject
				));
				if (!ownsSplineEndpoint) {
					return;
				}
				const pointAssignments = splineAssignmentsByTargetPointId.get(assignment.targetPoint.uuid) ?? [];
				pointAssignments.push(assignment);
				splineAssignmentsByTargetPointId.set(assignment.targetPoint.uuid, pointAssignments);
			});
			if (splineAssignmentsByTargetPointId.size <= 1) {
				return;
			}
			const rankedSplinePointGroups = [...splineAssignmentsByTargetPointId.values()]
				.map(pointAssignments => ({
					pointAssignments,
					evidence: Math.max(...pointAssignments.map(assignment => assignment.evidence)),
					referencePath: [...new Set(pointAssignments.map(assignment => assignment.referenceId))]
						.sort()
						.join('|'),
				}))
				.sort((left, right) => (
					right.evidence - left.evidence
					|| left.referencePath.localeCompare(right.referencePath)
				));
			rankedSplinePointGroups.slice(1).forEach((group) => {
				group.pointAssignments.forEach((assignment) => {
					unsafeReferenceIds.add(assignment.referenceId);
				});
			});
		});

		return new Set([...unsafeReferenceIds].sort());
	}

	/**
	 * 按来源锚点统一目标端点。
	 *
	 * @param targetSketch 目标草图。
	 * @param targetAnchorBindings 来源锚点到目标端点的绑定表。
	 * @param preferredTargetPointUuids Rebuild 时应优先保留的既有目标点 UUID。
	 * @returns 已收口为单点绑定的原映射。
	 */
	public canonicalize(
		targetSketch: SketchGroup,
		targetAnchorBindings: TargetAnchorBindings,
		preferredTargetPointUuids?: ReadonlySet<string>,
	): TargetAnchorBindings {
		this.beginCanonicalization(
			targetSketch,
			targetAnchorBindings,
			preferredTargetPointUuids,
		).commit();
		return targetAnchorBindings;
	}

	/**
	 * 开启可提交或回滚的目标端点统一事务。
	 *
	 * @param targetSketch 目标草图。
	 * @param targetAnchorBindings 来源锚点到目标端点的绑定表。
	 * @param preferredTargetPointUuids Rebuild 时应优先保留的既有目标点 UUID。
	 * @returns 已应用候选拓扑的端点统一事务。
	 */
	public beginCanonicalization(
		targetSketch: SketchGroup,
		targetAnchorBindings: TargetAnchorBindings,
		preferredTargetPointUuids?: ReadonlySet<string>,
	): SketchReferenceTargetEndpointCanonicalization {
		const assignments: SketchReferenceEndpointCanonicalizationAssignment[] = [];
		const bindingChanges: SketchReferenceTargetAnchorBindingChange[] = [];
		targetAnchorBindings.forEach((targetPoints, sourceAnchorId) => {
			const uniqueTargetPoints = [
				...new Map(targetPoints.map(targetPoint => [targetPoint.uuid, targetPoint])).values(),
			];
			const candidatePointIds = new Set(uniqueTargetPoints.map(targetPoint => targetPoint.uuid));
			const hasExplicitCoincidentInsideCandidateGroup = uniqueTargetPoints.some(targetPoint => (
				targetPoint.constraints.getRelations().some(relation => (
					isPointCoincidentConstraint(relation)
					&& relation.getTargetsSnapshot().filter(point => candidatePointIds.has(point.uuid)).length > 1
				))
			));
			/**
			 * 候选点之间的显式重合表达可删除关系，不能降级为不可删除的共享点身份。
			 */
			if (hasExplicitCoincidentInsideCandidateGroup) {
				targetAnchorBindings.set(sourceAnchorId, uniqueTargetPoints);
				return;
			}
			const relationBearingTargetPoints = uniqueTargetPoints.filter(targetPoint => (
				targetPoint.constraints.getRelations().length > 0
			));
			const splineEndpointTargetPoints = uniqueTargetPoints.filter(targetPoint => (
				targetPoint.getConnectedEdgesSnapshot().some(edge => (
					edge instanceof SketchSplineObject || edge instanceof SketchStyleSplineObject
				))
			));
			const topologyProtectedTargetPoints = [
				...new Map(
					[...relationBearingTargetPoints, ...splineEndpointTargetPoints]
						.map(targetPoint => [targetPoint.uuid, targetPoint]),
				).values(),
			];
			/**
			 * 多个候选点分别持有关系或样条端点身份时，无法在本模块内安全迁移全部目标，
			 * 保持独立等待 replacement/fallback 诊断。仅一个受保护点时优先保留该点，
			 * 其他普通边端点仍可合入真实共享拓扑。
			 */
			if (topologyProtectedTargetPoints.length > 1) {
				if (splineEndpointTargetPoints.length > 0) {
					throw new Error(`转换实体引用样条共享端点缺少可复用 keeper，必须执行 replacement rebuild: ${sourceAnchorId}`);
				}
				targetAnchorBindings.set(sourceAnchorId, uniqueTargetPoints);
				return;
			}
			const canonicalPoint = topologyProtectedTargetPoints[0] ?? uniqueTargetPoints.find(targetPoint => (
				preferredTargetPointUuids?.has(targetPoint.uuid)
			)) ?? uniqueTargetPoints[0];
			if (!canonicalPoint) {
				return;
			}

			uniqueTargetPoints
				.filter(targetPoint => targetPoint !== canonicalPoint)
				.forEach((targetPoint) => {
					if (targetPoint.constraints.getRelations().length > 0) {
						throw new Error(`转换实体引用端点在拓扑统一前已存在约束: ${sourceAnchorId}`);
					}
					const connectedEdges = targetPoint.getConnectedEdgesSnapshot();
					if (connectedEdges.length <= 0) {
						throw new Error(`转换实体引用端点缺少所属曲线: ${sourceAnchorId}`);
					}
					assignments.push({
						sourcePoint: targetPoint,
						canonicalPoint,
						connectedEdges,
					});
				});

			if (uniqueTargetPoints.length > 1) {
				bindingChanges.push({
					sourceAnchorId,
					previousTargetPoints: uniqueTargetPoints,
					canonicalPoint,
					currentProjectionPosition: this.resolveCurrentProjectionPosition(
						sourceAnchorId,
						uniqueTargetPoints,
						preferredTargetPointUuids,
					),
				});
			}
		});
		const canonicalPointsToPromote = [
			...new Map(
				[
					...bindingChanges.map(change => change.canonicalPoint),
					...[...targetAnchorBindings.values()]
						.map(points => [...new Map(points.map(point => [point.uuid, point])).values()])
						.filter(points => points.length === 1)
						.flat(),
				]
					.filter(targetPoint => targetPoint.getConnectedEdgesSnapshot().length > 1)
					.map(targetPoint => [targetPoint.uuid, targetPoint]),
			).values(),
		];
		return new SketchReferenceTargetEndpointCanonicalization({
			targetSketch,
			targetAnchorBindings,
			assignments,
			bindingChanges,
			canonicalPointsToPromote,
		});
	}

	/**
	 * 解析同一来源锚点在当前重建批次中的权威坐标。
	 *
	 * Rebuild 会优先保留 in-place 点的对象身份，但 replacement 点由当前投影结果新建，
	 * 因而在混合端点组中应由非 preferred 候选提供坐标。若存在多个当前候选，它们
	 * 必须在闭合容差内一致，禁止通过执行顺序静默选择不同位置。
	 *
	 * @param sourceAnchorId 来源锚点 ID。
	 * @param targetPoints 当前锚点关联的全部目标候选点。
	 * @param preferredTargetPointUuids 应保留身份的既有点集合。
	 * @returns 当前投影锚点世界坐标。
	 */
	private resolveCurrentProjectionPosition(
		sourceAnchorId: string,
		targetPoints: readonly SketchPointObject[],
		preferredTargetPointUuids?: ReadonlySet<string>,
	): readonly [number, number, number] {
		const currentProjectionPoints = preferredTargetPointUuids
			? targetPoints.filter(point => !preferredTargetPointUuids.has(point.uuid))
			: [...targetPoints];
		const coordinateCandidates = currentProjectionPoints.length > 0
			? currentProjectionPoints
			: targetPoints;
		const authoritativePoint = coordinateCandidates[0];
		if (!authoritativePoint) {
			throw new Error(`转换实体引用来源锚点缺少坐标候选: ${sourceAnchorId}`);
		}
		const authoritativePosition = authoritativePoint.getPosition();
		const toleranceSquared = SKETCH_GEOMETRY_TOLERANCE_CONFIG.collectionClosurePosition ** 2;
		/**
		 * topology 锚点已经由 owner + canonical vertex 唯一确认物理端点。
		 * 导入模型采样曲线拟合后允许端点存在数值偏差，不能再用草图闭合容差否定同一拓扑身份；
		 * 非 topology 来源仍保留严格坐标校验，防止普通草图点被错误合并。
		 */
		const requiresCoordinateConsistency = !sourceAnchorId.startsWith('topology:');
		if (requiresCoordinateConsistency && coordinateCandidates.some(point => (
			point.getPosition().distanceToSquared(authoritativePosition) > toleranceSquared
		))) {
			throw new Error(`转换实体引用同一来源锚点存在不一致投影坐标: ${sourceAnchorId}`);
		}
		return [
			authoritativePosition.x,
			authoritativePosition.y,
			authoritativePosition.z,
		];
	}
}
